import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineKey, HiOutlinePlus, HiOutlineBookOpen, HiOutlineArrowPath } from 'react-icons/hi2';
import { VscMcp } from 'react-icons/vsc';
import cloudApi from '../../../services/cloudApi';
import { useAuth } from '../../../contexts/AuthContext';
import { isClaudeGrant, isChatGptGrant } from '../../../utils/mcpClients';
import useOAuthGrants from '../../../hooks/useOAuthGrants';
import RichTooltip from './RichTooltip';
import ConnectedClientRow from './ConnectedClientRow';
import { CardBodySkeleton } from './CardSkeleton';
import McpSetupGuideModal from './McpSetupGuideModal';
import CreateTokenModal from '../McpView/CreateTokenModal';
import McpUpsellModal from '../McpView/McpUpsellModal';

// The "MCP" integration card — AI agents that authenticate with an access token
// (Claude Code, Cursor, anything that can set a header). Claude and ChatGPT have
// their own cards; any other signed-in client still lands here.
// The admin-only McpView page keeps the marketing version of this.

const formatRelativeTime = (dateString) => {
  const date = cloudApi.toLocalDate(dateString);
  if (!date) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
};

const isReadOnlyScopes = (scopes) => Array.isArray(scopes) && scopes.length === 1 && scopes[0] === 'read';

const McpIntegrationCard = () => {
  const { subscription, subscriptionLoading } = useAuth();
  // Connecting AI agents is a paid feature — the backend gates token creation
  // with 402 PLAN_REQUIRED. Detect the free plan up front so the CTA sells the
  // upgrade instead of failing after a filled-in form.
  const planName = subscription?.subscription?.plan?.name || 'free';
  const isFreePlan = planName === 'free';

  const otherClient = useCallback((g) => !isClaudeGrant(g) && !isChatGptGrant(g), []);
  const { grants, reload: reloadGrants, removeGrant } = useOAuthGrants(otherClient);
  const [tokens, setTokens] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnectingGrant, setDisconnectingGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [revokeConfirmId, setRevokeConfirmId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  // Plaintext token from a creation in this session — lets the setup guide show
  // real, paste-ready snippets (the token is never retrievable again).
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionTokenId, setSessionTokenId] = useState(null);
  // Whether the OAuth authorization server is live (OAUTH_ENABLED on the
  // backend) — CreateTokenModal tailors its Claude instructions to it.
  const [oauthAvailable, setOauthAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${cloudApi.getApiUrl()}/.well-known/oauth-authorization-server`)
      .then((res) => { if (!cancelled) setOauthAvailable(res.ok); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadTokens = useCallback(async () => {
    try {
      const data = await cloudApi.getMcpTokens();
      setTokens(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load MCP tokens:', err);
      setError(err.message || 'Failed to load tokens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Sign-in connections happen inside the other app — this picks up a fresh one.
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadTokens(), reloadGrants()]);
    setRefreshing(false);
  };

  const handleDisconnectGrant = async (grantId) => {
    setDisconnectingGrant(grantId);
    setError(null);
    try {
      await cloudApi.revokeOAuthGrant(grantId);
      removeGrant(grantId);
    } catch (err) {
      setError(err.message || 'Failed to disconnect.');
    } finally {
      setDisconnectingGrant(null);
    }
  };

  // Free workspaces get the upsell; paid ones go straight to the create flow.
  // While the plan is still loading, default to the create flow — the modal's
  // own 402 handling is the backstop if the plan turns out to be free.
  const handleCreateClick = () => {
    if (isFreePlan && !subscriptionLoading) {
      setShowUpsell(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const handleTokenCreated = (result) => {
    setSessionToken(result.token);
    setSessionTokenId(result.tokenRecord?.id || null);
    loadTokens();
  };

  const handleRevoke = async (tokenId) => {
    setRevokingId(tokenId);
    setError(null);
    try {
      await cloudApi.revokeMcpToken(tokenId);
      // Revoked token is dead — stop offering it in the setup-guide snippets.
      if (sessionTokenId === tokenId) {
        setSessionToken(null);
        setSessionTokenId(null);
      }
      setRevokeConfirmId(null);
      await loadTokens();
    } catch (err) {
      console.error('Failed to revoke token:', err);
      setError(err.message || 'Failed to revoke token.');
    } finally {
      setRevokingId(null);
    }
  };

  const isConnected = tokens.length > 0 || grants.length > 0;

  return (
    <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 p-6 flex flex-col transition-all duration-300 hover:shadow-xl">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/60 dark:to-gray-900/40 shadow-md shadow-gray-200/30 dark:shadow-black/20 ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
            <VscMcp className="w-7 h-7 text-cyan-500 dark:text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">MCP</h3>
              <RichTooltip
                content={
                  <div className="space-y-2.5 text-xs">
                    <p className="text-sm font-semibold text-white">How MCP Works</p>
                    <div className="space-y-2 text-gray-300 leading-relaxed">
                      <div className="flex gap-2">
                        <span className="text-cyan-400 font-bold flex-shrink-0">1.</span>
                        <span>Create an access token for your AI agent</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-cyan-400 font-bold flex-shrink-0">2.</span>
                        <span>Paste the ready-made snippet into Claude Code, Cursor, or any MCP client</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-cyan-400 font-bold flex-shrink-0">3.</span>
                        <span>The agent can query your tables, run automations, and start extractions</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-2xs pt-1.5 border-t border-gray-700">
                      MCP (Model Context Protocol) is the open standard AI agents use to connect to tools.
                      Tokens are shown once and revocable anytime.
                    </p>
                  </div>
                }
                width={300}
              >
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                </svg>
              </RichTooltip>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Connect any AI agent to your workspace</p>
          </div>
        </div>

        {isConnected && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
            Connected
          </span>
        )}
      </div>

      {loading ? (
        <CardBodySkeleton />
      ) : isConnected ? (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
            AI apps connected to this workspace — by sign-in, or with an access token. Disconnecting
            or revoking cuts that client off immediately.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          {grants.length > 0 && (
            <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04] mb-3">
              {grants.map((grant) => (
                <ConnectedClientRow
                  key={grant.id}
                  grant={grant}
                  onDisconnect={handleDisconnectGrant}
                  disconnecting={disconnectingGrant === grant.id}
                />
              ))}
            </div>
          )}

          {tokens.length > 0 && (
          <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04]">
            {tokens.map((token) => (
              <div key={token.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-white dark:bg-gray-800 ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
                  <HiOutlineKey className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{token.name}</span>
                    <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {isReadOnlyScopes(token.scopes) ? 'Read-only' : 'Full access'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                    <code className="font-mono">{token.tokenPrefix}...</code>
                    {' · '}
                    {token.lastUsedAt ? `last used ${formatRelativeTime(token.lastUsedAt)}` : 'never used'}
                  </p>
                </div>
                {revokeConfirmId === token.id ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleRevoke(token.id)}
                      disabled={revokingId === token.id}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
                    >
                      {revokingId === token.id ? 'Revoking…' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setRevokeConfirmId(null)}
                      disabled={revokingId === token.id}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setRevokeConfirmId(token.id)}
                    className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-300 hover:text-red-600 dark:hover:border-red-700 dark:hover:text-red-400 transition-colors"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-auto pt-3 flex-wrap">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              <HiOutlineArrowPath className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
            <button
              onClick={() => setShowSetupGuide(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
            >
              <HiOutlineBookOpen className="w-3.5 h-3.5" />
              Setup Guide
            </button>
            <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              <HiOutlinePlus className="w-4 h-4" />
              New token
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            Give Claude Code, Cursor, or any MCP-compatible agent access to this workspace — query and
            clean your tables, run automations, and launch extractions by asking. Connecting Claude or
            ChatGPT? They sign in instead — use their own cards above.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          <div className="mt-auto flex items-center gap-2 flex-wrap">
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              <HiOutlineKey className="w-4 h-4" />
              Create access token
            </button>
            <button
              onClick={() => setShowSetupGuide(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
            >
              <HiOutlineBookOpen className="w-3.5 h-3.5" />
              Setup Guide
            </button>
          </div>
        </div>
      )}

      <CreateTokenModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleTokenCreated}
        onPlanRequired={() => { setShowCreateModal(false); setShowUpsell(true); }}
        oauthAvailable={oauthAvailable}
      />

      <McpSetupGuideModal
        isOpen={showSetupGuide}
        onClose={() => setShowSetupGuide(false)}
        sessionToken={sessionToken}
      />

      <McpUpsellModal isOpen={showUpsell} onClose={() => setShowUpsell(false)} />
    </div>
  );
};

export default McpIntegrationCard;
